import Link from 'next/link';
import Pin from '@monofront/design-system/visuals/Icons/Old/Pin';
import {
  FinancialStatus,
  FulfillmentStatus,
  IOrder,
  OrderStatus,
} from '@monofront/types';
import { useTranslations } from 'next-intl';
import {
  getOrderStatus,
  getCarrierModalMessage,
  getIsShipUp,
  getFinancialStatus,
} from '@monofront/business/utils';
import { PreparingOrderNoticeModal } from '@components/Order/PreparingOrderNoticeModal/PreparingOrderNoticeModal';
import { useState } from 'react';

interface ITrackingLinkProps {
  order: IOrder;
}

export const TrackingLink: React.FC<ITrackingLinkProps> = ({ order }) => {
  const tAccount = useTranslations('Account');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const orderStatus = getOrderStatus(order);
  const financialStatus = getFinancialStatus(order);

  const isOrderCancelled = orderStatus === OrderStatus.cancelled;
  const isOrderRefunded = financialStatus === FinancialStatus.REFUNDED;
  const isOrderShipped = [
    FulfillmentStatus.fulfilled,
    FulfillmentStatus.partial,
  ].includes(order.fulfillmentStatus);
  const isShipUp = getIsShipUp(order.noteAttributes);

  // LF-3411: Tracking links before 2023-12-20 where not updated in DB
  const isTrackingLinkArchived =
    new Date(order.createdAt).valueOf() < new Date('2023-12-20').valueOf();

  const firstFulfillment = order.fulfillments?.[0];
  const trackingUrl = (firstFulfillment?.trackingUrl || '').startsWith('http')
    ? firstFulfillment?.trackingUrl
    : '';
  const shipupTrackingUrl =
    isShipUp && order.name
      ? `/pages/tracking?orderNumber=${order.name.replace('#', '')}`
      : '';

  const isTrackingCtaDisplayed =
    (isOrderShipped && Boolean(trackingUrl)) || Boolean(shipupTrackingUrl);

  const modalTranslationKey = (getCarrierModalMessage(order.noteAttributes) ||
    'defaultMessage') as Extract<
    keyof IntlMessages['Account']['orderTrackingModal'],
    string
  >;

  if (isOrderCancelled || isOrderRefunded || isTrackingLinkArchived)
    return null;

  return (
    <>
      {!isTrackingCtaDisplayed && (
        <>
          <PreparingOrderNoticeModal
            isOpen={isModalOpen}
            hide={setIsModalOpen.bind(this, false)}
          >
            {tAccount.rich(`orderTrackingModal.${modalTranslationKey}`, {
              p: (children) => <p className="tw-mb-2">{children}</p>,
            })}
          </PreparingOrderNoticeModal>
          <button
            type="button"
            className="tw-lf-button tw-lf-button-secondary tw-lf-button-md tw-gap-0.5"
            data-carriermessage={modalTranslationKey}
            onClick={setIsModalOpen.bind(this, true)}
          >
            <Pin aria-hidden />
            {tAccount('follow')}
          </button>
        </>
      )}

      {isTrackingCtaDisplayed && shipupTrackingUrl ? (
        <Link
          href={shipupTrackingUrl}
          target="_blank"
          className="tw-lf-button tw-lf-button-secondary tw-lf-button-md tw-gap-0.5"
          data-testid="TrackingLink-go-to-trackingLink-shipup-follow"
        >
          <Pin aria-hidden />
          {tAccount('follow')}
        </Link>
      ) : (
        isTrackingCtaDisplayed &&
        trackingUrl && (
          <a
            href={trackingUrl}
            target="_blank"
            data-testid="TrackingLink-go-to-trackingLink-follow"
            rel="noreferrer"
            className="tw-lf-button tw-lf-button-secondary tw-lf-button-md tw-gap-0.5"
          >
            <Pin aria-hidden />
            {tAccount('follow')}
          </a>
        )
      )}
    </>
  );
};
